import { KeyValuePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, inject } from '@angular/core';
import { orderBy } from 'lodash-es';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';

import { BusinessAccountAddress, CustomerAddress } from '@shared/models';
import { toTitleCase } from '@shared/utils';

import { DetailsItemComponent } from '../../details-item/details-item.component';

@Component({
  selector: 'app-address-view',
  templateUrl: 'address-view.component.html',
  styleUrls: ['address-view.component.scss'],
  imports: [DetailsItemComponent, KeyValuePipe, RktIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressViewComponent {
  ref = inject(ChangeDetectorRef);

  private _addresses: (CustomerAddress | BusinessAccountAddress)[] = [];

  @Input()
  set addresses(value: (CustomerAddress | BusinessAccountAddress)[]) {
    this._addresses = value;
    this._addressesByType = this.groupAddresses(value);
  }

  get addresses(): (CustomerAddress | BusinessAccountAddress)[] {
    return this._addresses;
  }

  private _addressesByType: Record<string, (CustomerAddress | BusinessAccountAddress)[]> = {};

  get addressesByType() {
    return this._addressesByType;
  }

  private groupAddresses(addresses: (CustomerAddress | BusinessAccountAddress)[]) {
    return {
      physicalAddresses: orderBy(addresses?.filter(({ type }) => type === 'PHYSICAL') ?? [], ['primary'], ['desc']),
      mailingAddresses: orderBy(addresses?.filter(({ type }) => type === 'MAILING') ?? [], ['primary'], ['desc']),
      shippingAddresses: orderBy(addresses?.filter(({ type }) => type === 'SHIPPING') ?? [], ['primary'], ['desc']),
    };
  }

  @Input() isLoading = true;

  public keepOriginalOrder(): number {
    return 1;
  }

  public getAddressIconName(key: string) {
    switch (key) {
      case 'physicalAddresses':
        return 'location';
      case 'mailingAddresses':
        return 'mailing';
      case 'shippingAddresses':
        return 'shipping';
      default:
        return 'location';
    }
  }

  public getAddressTitle(key: string) {
    const type = toTitleCase(key.split('Addresses')[0]);
    return `${type} Address`;
  }

  public getAddressLabel(address: CustomerAddress | BusinessAccountAddress, index: number) {
    return address.primary ? 'Primary Address' : `Address ${index + 1}`;
  }

  public getContactFullName(address: CustomerAddress | BusinessAccountAddress) {
    const { firstName, middleName, lastName } = address.name ?? {};
    if (!firstName && !middleName && !lastName) return '';
    return `${firstName}${middleName ? ' ' + middleName : ''} ${lastName}`;
  }
}
